import { PlaceSummary } from '../types/PlaceSummary';

/**
 * The shape of the response returned by the service function.
 */
export interface ServiceResponse {
  result: PlaceSummary[];
  error?: any;
  status?: number;
}

/**
 * Type for a function that fetches places given a query string.
 */
export type FetchPlacesFn = (query: string) => Promise<ServiceResponse>;

/**
 * Filters an array of Place objects by checking if any of their
 * district_borough, region, or county_unitary fields include the
 * provided locationTerm (case-insensitive).
 *
 * @param results - Array of Place objects to filter
 * @param locationTerm - The term to filter by
 * @returns A subset of results whose combined text includes locationTerm
 */
export function filterByLocation(results: PlaceSummary[], locationTerm: string): PlaceSummary[] {
  return results.filter(place => {
    const searchableText = [place.district_borough, place.region, place.county_unitary]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(locationTerm.toLowerCase());
  });
}

/**
 * Attempts to search places using a primary query (all but the last term),
 * and falls back to using the last term if the primary returns no results.
 * If the fallback yields results and more than one term was provided,
 * it will also apply filterByLocation using the fallback term.
 *
 * @param queryWords - Array of individual search terms from the user
 * @param serviceFn - Function that performs the API call, returning a ServiceResponse
 * @returns A ServiceResponse containing either an error or the best-matching result array
 */
export async function searchWithFallback(
  queryWords: string[],
  serviceFn: FetchPlacesFn,
): Promise<ServiceResponse> {
  // Clone to avoid mutating the original array
  const terms = [...queryWords];
  // The last term is used as the secondary (fallback) search
  const secondary = terms.pop() ?? '';
  // Primary search is all preceding terms, or the secondary if none exist
  const primary = terms.length > 0 ? terms.join(' ') : secondary;

  // 1) Try primary search
  const firstResponse = await serviceFn(primary);

  if (firstResponse.error) {
    return { error: firstResponse.error, status: firstResponse?.status, result: [] };
  }

  if (firstResponse.result.length > 0) {
    if (queryWords.length <= 1) {
      return { result: firstResponse.result };
    }

    // More than 2 search query terms used:
    // - lets try and filter down the list for a closer match
    const filtered = filterByLocation(firstResponse.result, secondary);
    return { result: filtered.length > 0 ? filtered : firstResponse.result };
  }

  // 2) Fallback: search using the secondary term
  const fallbackResponse = await serviceFn(secondary);

  if (fallbackResponse.error) {
    return { error: fallbackResponse.error, status: fallbackResponse?.status, result: [] };
  }

  const { result: fallbackResults } = fallbackResponse;

  // If multiple terms were provided, attempt to refine via filtering
  if (fallbackResults.length > 0 && queryWords.length > 1) {
    const filtered = filterByLocation(fallbackResults, secondary);
    return { result: filtered.length > 0 ? filtered : fallbackResults };
  }

  // Return whatever the fallback yielded (even if empty)
  return { result: fallbackResults };
}
